import { Box, Transition } from '@mantine/core';
import { IconArrowBarRight } from '@tabler/icons-react';
import { SidebarPillButton } from './sidebar/SidebarPillButton';
import { STANDARD_SIDEBAR_WIDTH } from './useDragAnimation';

const SCALE_TRANSITION = 'transform 0.15s ease-out';
const SIDEBAR_TRANSITION = `flex-grow 0.35s ease-out, opacity 0.2s ease-out, ${SCALE_TRANSITION}`;
const INNER_COLLAPSE_TRANSITION = 'transform 0.25s ease-out';

type Props = {
  children: React.ReactNode;
  style?: React.CSSProperties;
  width: number;
  isDragging?: boolean;
  collapsed?: boolean;
  fullScreen?: boolean;
  scalePreview?: boolean;
  onExitFullScreen?: () => void;
};

export function AppSidebar({
  children,
  style,
  width,
  isDragging,
  collapsed,
  fullScreen,
  scalePreview,
  onExitFullScreen,
}: Props) {
  const transition = isDragging ? SCALE_TRANSITION : SIDEBAR_TRANSITION;

  return (
    <Box
      h="100%"
      pos="relative"
      display="flex"
      style={{
        backgroundColor: 'rgba(255, 119, 28, 0.4)',
        borderRadius:
          'var(--mantine-spacing-xxl) var(--mantine-spacing-xxl) var(--mantine-spacing-xxxl) var(--mantine-spacing-xxxl)',
        transition,
        overflow: 'hidden',
        flexGrow: fullScreen ? 1 : 0,
        flexShrink: 0,
        flexBasis: collapsed ? 0 : width,
        opacity: collapsed ? 0 : 1,
        transform: scalePreview ? 'scale(0.97)' : 'scale(1)',
        transformOrigin: 'center center',
        ...style,
      }}
    >
      <Transition
        mounted={!!fullScreen && !!onExitFullScreen}
        transition="fade"
        duration={350}
        timingFunction="ease-in"
      >
        {(styles: React.CSSProperties) => (
          <SidebarPillButton
            onClick={onExitFullScreen!}
            icon={<IconArrowBarRight size={16} color="var(--mantine-color-orange-6)" />}
            label="Show content"
            style={{
              ...styles,
              position: 'absolute',
              top: 'var(--mantine-spacing-xxl)',
              left: 'var(--mantine-spacing-xxl)',
            }}
          />
        )}
      </Transition>
      <Box
        h="100%"
        w="100%"
        pos="relative"
        style={{
          borderRadius:
            'var(--mantine-spacing-xxl) var(--mantine-spacing-xxl) var(--mantine-spacing-xxxl) var(--mantine-spacing-xxxl)',
          overflow: 'hidden',
          transition: isDragging ? 'none' : INNER_COLLAPSE_TRANSITION,
          minWidth: fullScreen ? undefined : `${STANDARD_SIDEBAR_WIDTH}px`,
          transform: collapsed ? 'translateX(100%)' : 'translateX(0)',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
